import React, { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  Button,
  Card,
  CellText,
  EmptyState,
  Modal,
  Pagination,
  Table,
  Trash01,
  Users01,
} from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";

const qs = require("qs");

const PAGE_SIZE = 25;
const DT_COLUMNS = ["name", "email", "total_events", "city", "state", "lifetime_value", "signed_up_at"];

const buildParams = (draw, page, sort) => {
  const columns = {};
  DT_COLUMNS.forEach((data, i) => {
    columns[i] = { data, searchable: false, orderable: true, search: { value: "", regex: false } };
  });

  return {
    draw,
    start: (page - 1) * PAGE_SIZE,
    length: PAGE_SIZE,
    search: { value: "", regex: false },
    columns,
    order: { 0: { column: Math.max(0, DT_COLUMNS.indexOf(sort.key)), dir: sort.direction } },
  };
};

const orDash = (value) => value || "—";

const SegmentFansTable = ({ dataUrl, isManual, onFanRemoved }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: "email", direction: "asc" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [fanToRemove, setFanToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const drawRef = useRef(0);

  useEffect(() => {
    let ignore = false;
    drawRef.current += 1;
    setLoading(true);
    setError(false);

    client.get(dataUrl, {
      params: buildParams(drawRef.current, page, sort),
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "brackets" }),
    })
      .then((res) => {
        if (ignore) return;
        setRows(res.data.data || []);
        setTotal(res.data.recordsFiltered || 0);
      })
      .catch(() => {
        if (ignore) return;
        setRows([]);
        setTotal(0);
        setError(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => { ignore = true; };
  }, [dataUrl, page, sort, reloadKey]);

  const removeFan = () => {
    if (removing) return;
    setRemoving(true);
    client.delete(fanToRemove.remove_url, { headers: { Accept: "application/json" } })
      .then((res) => {
        onFanRemoved(res.data);
        toastSuccess(`Removed ${fanToRemove.name || fanToRemove.email} from this segment`);
        setFanToRemove(null);
        if (rows.length === 1 && page > 1) {
          setPage(page - 1);
        } else {
          setReloadKey((key) => key + 1);
        }
      })
      .catch(() => toastError("Could not remove fan, please try again"))
      .finally(() => setRemoving(false));
  };

  const closeRemoveModal = () => {
    if (!removing) setFanToRemove(null);
  };

  const columns = [
    {
      key: "name",
      header: "Name",
      primary: true,
      sortable: true,
      render: (row) => (
        <a href={row.url} className="text-reset text-decoration-none">
          <CellText primary={orDash(row.name)} />
        </a>
      ),
    },
    { key: "email", header: "Email", sortable: true, render: (row) => orDash(row.email) },
    { key: "total_events", header: "Total events", align: "right", sortable: true, render: (row) => row.total_events.toLocaleString() },
    { key: "city", header: "City", sortable: true, render: (row) => orDash(row.city) },
    { key: "state", header: "State", sortable: true, render: (row) => orDash(row.state) },
    { key: "lifetime_value", header: "Lifetime value", align: "right", sortable: true, render: (row) => row.lifetime_value },
    { key: "signed_up_at", header: "Date signed up", sortable: true, render: (row) => orDash(row.signed_up_at) },
    ...(isManual ? [{
      key: "remove",
      header: "",
      align: "right",
      render: (row) => (row.remove_url ? (
        <Button
          variant="tertiary"
          size="sm"
          icon={Trash01}
          aria-label={`Remove ${row.name || row.email} from segment`}
          onClick={() => setFanToRemove(row)}
        />
      ) : null),
    }] : []),
  ];

  return (
    <Card padding="none">
      <Table
        columns={columns}
        data={rows}
        getRowKey={(row) => row.id}
        loading={loading}
        loadingLabel="Loading fans"
        stickyFirstColumn
        stickyLastColumn={isManual}
        error={error ? "Couldn't load fans. Refresh the page to try again." : undefined}
        sort={sort}
        onSortChange={(key, direction) => { setPage(1); setSort({ key, direction }); }}
        empty={<EmptyState size="sm" icon={Users01} title="No fans in this segment yet." />}
      />
      {!loading && total > PAGE_SIZE ? (
        <div style={{ padding: "12px 24px", borderTop: "1px solid var(--sl-border-secondary)" }}>
          <Pagination currentPage={page} totalPages={Math.ceil(total / PAGE_SIZE)} onPageChange={setPage} />
        </div>
      ) : null}
      <Modal
        open={!!fanToRemove}
        type="error"
        icon={AlertCircle}
        title="Remove fan from segment?"
        description={fanToRemove ? `${fanToRemove.name || fanToRemove.email} will no longer be part of this segment.` : null}
        onClose={closeRemoveModal}
        footer={(
          <>
            <Button variant="secondary" disabled={removing} onClick={closeRemoveModal}>Cancel</Button>
            <Button variant="destructive" disabled={removing} onClick={removeFan}>Remove</Button>
          </>
        )}
      />
    </Card>
  );
};

export default SegmentFansTable;
